/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Geometry, Face3, Color, Vector2, Vector3 } from 'three';
/**
 * special geometry that represent a Rhombicuboctahedron
 */
export class Rhombicuboctahedron extends Geometry {
    constructor(size) {
        super();
        this.size = size;
        this._create();
        this.computeFaceNormals();
        this.computeVertexNormals();
    }
    _create() {
        this._createPoints();
        this._createFaces();
        return this;
    }
    // uv : [su,eu,sv,ev]
    _pushFace4(fi, color, uv) {
        this.faces.push(new Face3(fi[2], fi[1], fi[0], undefined, color));
        this.faces.push(new Face3(fi[1], fi[2], fi[3], undefined, color));
        if (!!uv) {
            const su = uv[0];
            const eu = uv[1];
            const sv = uv[2];
            const ev = uv[3];
            this.faceVertexUvs[0].push([
                new Vector2(su, sv),
                new Vector2(eu, ev),
                new Vector2(su, ev),
            ]);
            this.faceVertexUvs[0].push([
                new Vector2(eu, ev),
                new Vector2(su, sv),
                new Vector2(eu, sv),
            ]);
        }
    }
    _pushFace3(fi, color) {
        this.faces.push(new Face3(fi[0], fi[1], fi[2], undefined, color));
    }
    _createFaces() {
        // frontals    [7,19,10,22]
        this._pushFace4([6, 0, 18, 12], new Color(0xff0000), [0, 1 / 3, 2 / 3, 1]); // +x
        this._pushFace4([3, 9, 15, 21], new Color(0xff0000), [1 / 3, 2 / 3, 2 / 3, 1]); // -x
        this._pushFace4([1, 4, 13, 16], new Color(0x00ff00), [0, 1 / 3, 1 / 3, 2 / 3]); // +y
        this._pushFace4([10, 7, 22, 19], new Color(0x00ff00), [1 / 3, 2 / 3, 1 / 3, 2 / 3]); // -y
        this._pushFace4([5, 2, 11, 8], new Color(0x0000ff), [0, 1 / 3, 0, 1 / 3]); // +z
        this._pushFace4([14, 17, 20, 23], new Color(0x0000ff), [1 / 3, 2 / 3, 0, 1 / 3]); // -z
        // edges
        this._pushFace4([0, 1, 12, 13], new Color(0xffff00)); // xy
        this._pushFace4([3, 15, 4, 16], new Color(0xffff00)); // -xy
        this._pushFace4([6, 18, 7, 19], new Color(0xffff00)); // x-y
        this._pushFace4([9, 10, 21, 22], new Color(0xffff00)); // -x-y
        this._pushFace4([0, 6, 2, 8], new Color(0xff00ff)); // xz
        this._pushFace4([3, 5, 9, 11], new Color(0xff00ff)); // -xz
        this._pushFace4([12, 14, 18, 20], new Color(0xff00ff)); // x-z
        this._pushFace4([15, 21, 17, 23], new Color(0xff00ff)); // -x-z
        this._pushFace4([1, 2, 4, 5], new Color(0x00ffff)); // yz
        this._pushFace4([7, 10, 8, 11], new Color(0x00ffff)); // -yz
        this._pushFace4([13, 16, 14, 17], new Color(0x00ffff)); // y-z
        this._pushFace4([19, 20, 22, 23], new Color(0x00ffff)); // -y-z
        // corners
        this._pushFace3([0, 1, 2], new Color(0xffffff)); // +++
        this._pushFace3([5, 4, 3], new Color(0xffffff)); // -++
        this._pushFace3([8, 7, 6], new Color(0xffffff)); // +-+
        this._pushFace3([9, 10, 11], new Color(0xffffff)); // --+
        this._pushFace3([14, 13, 12], new Color(0xffffff)); // ++-
        this._pushFace3([15, 16, 17], new Color(0xffffff)); // -+-
        this._pushFace3([18, 19, 20], new Color(0xffffff)); // +--
        this._pushFace3([23, 22, 21], new Color(0xffffff)); // ---
    }
    _createPoints() {
        const s = this.size;
        const b = s * 0.1; // (1/(Math.SQRT2+2)) ;
        const a = s * 0.4; // ((Math.SQRT2/(Math.SQRT2+2)) / 2) ;
        const c = a + b;
        this.vertices.push(new Vector3(c, a, a)); // 0
        this.vertices.push(new Vector3(a, c, a)); // 1
        this.vertices.push(new Vector3(a, a, c)); // 2
        this.vertices.push(new Vector3(-c, a, a)); // 3
        this.vertices.push(new Vector3(-a, c, a)); // 4
        this.vertices.push(new Vector3(-a, a, c)); // 5
        this.vertices.push(new Vector3(c, -a, a)); // 6
        this.vertices.push(new Vector3(a, -c, a)); // 7
        this.vertices.push(new Vector3(a, -a, c)); // 8
        this.vertices.push(new Vector3(-c, -a, a)); // 9
        this.vertices.push(new Vector3(-a, -c, a)); // 10
        this.vertices.push(new Vector3(-a, -a, c)); // 11
        this.vertices.push(new Vector3(c, a, -a)); // 12
        this.vertices.push(new Vector3(a, c, -a)); // 13
        this.vertices.push(new Vector3(a, a, -c)); // 14
        this.vertices.push(new Vector3(-c, a, -a)); // 15
        this.vertices.push(new Vector3(-a, c, -a)); // 16
        this.vertices.push(new Vector3(-a, a, -c)); // 17
        this.vertices.push(new Vector3(c, -a, -a)); // 18
        this.vertices.push(new Vector3(a, -c, -a)); // 19
        this.vertices.push(new Vector3(a, -a, -c)); // 20
        this.vertices.push(new Vector3(-c, -a, -a)); // 21
        this.vertices.push(new Vector3(-a, -c, -a)); // 22
        this.vertices.push(new Vector3(-a, -a, -c)); // 23
    }
}
//# sourceMappingURL=Rhombicuboctahedron.js.map